import {RenderAlert} from "components/src/common/RenderAlert";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {EmailUtils} from "components/src/utils/EmailUtils";
import {PhoneUtils} from "components/src/utils/PhoneUtils";
import {StringUtils} from "components/src/utils/StringUtils";
import {Bin as DeleteIcon, Plus as AddIcon} from "iconoir-react";
import {useState} from "react";
import Button from "react-bootstrap/Button";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Modal from "react-bootstrap/Modal";
import {AddressBookEntry} from "./AddressBook";

interface AddressBookEntryDialogProps {
    /**
     * The entry being edited, or undefined when adding a new entry
     */
    editEntry?: AddressBookEntry;
    /**
     * The (lower-cased) emails already present, used to prevent duplicates
     */
    existingEmails: string[];
    /**
     * Called with the new/updated entry and the original email (so a changed email replaces the right entry)
     */
    onSave: (entry: AddressBookEntry, originalEmail?: string) => void;
    onClose: () => void;
}

export const AddressBookEntryDialog = ({editEntry, existingEmails, onSave, onClose}: AddressBookEntryDialogProps) =>
{
    const {t} = useTranslationWithOverride();

    const [email, setEmail] = useState<string>(editEntry?.email ?? "");
    const [firstName, setFirstName] = useState<string>(editEntry?.firstName ?? "");
    const [lastName, setLastName] = useState<string>(editEntry?.lastName ?? "");
    const [organizationName, setOrganizationName] = useState<string>(editEntry?.organizationName ?? "");
    // always show at least one phone number input
    const [phoneNumbers, setPhoneNumbers] = useState<string[]>(
            editEntry?.phoneNumbers && editEntry.phoneNumbers.length > 0 ? editEntry.phoneNumbers : [""]);
    const [errorMessage, setErrorMessage] = useState<string>();

    const setPhoneNumber = (index: number, value: string) => {
        setPhoneNumbers(numbers => numbers.map((number, i) => i === index ? value : number));
    }

    const addPhoneNumber = () => {
        setPhoneNumbers(numbers => [...numbers, ""]);
    }

    const removePhoneNumber = (index: number) => {
        setPhoneNumbers(numbers => numbers.filter((_, i) => i !== index));
    }

    const save = () => {
        const trimmedEmail = email.trim();
        const trimmedPhoneNumbers = phoneNumbers.map(number => number.trim()).filter(number => !StringUtils.isEmpty(number));

        if (StringUtils.isBlank(trimmedEmail)) {
            setErrorMessage(t("address-book.email-required"));
            return;
        }

        if (!EmailUtils.isValidEmail(trimmedEmail)) {
            setErrorMessage(t("backend.portal.invalid-email-address"));
            return;
        }

        if (trimmedPhoneNumbers.length === 0) {
            setErrorMessage(t("address-book.phone-required"));
            return;
        }

        if (trimmedPhoneNumbers.some(number => !PhoneUtils.isValidPhoneNumber(number))) {
            setErrorMessage(t("backend.validation.invalid-phone-number"));
            return;
        }

        // when adding, or when the email was changed, it must not collide with an existing entry
        if (trimmedEmail.toLowerCase() !== editEntry?.email.toLowerCase()
                && existingEmails.includes(trimmedEmail.toLowerCase())) {
            setErrorMessage(t("address-book.email-exists"));
            return;
        }

        onSave({
            email: trimmedEmail,
            phoneNumbers: trimmedPhoneNumbers,
            organizationName: StringUtils.isEmpty(organizationName.trim()) ? undefined : organizationName.trim(),
            firstName: StringUtils.isEmpty(firstName.trim()) ? undefined : firstName.trim(),
            lastName: StringUtils.isEmpty(lastName.trim()) ? undefined : lastName.trim()
        }, editEntry?.email);
    }

    return (
        <Modal show={true} centered={true} onHide={onClose} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>
                    {editEntry === undefined ? t("address-book.add-contact") : t("address-book.edit-contact")}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <RenderAlert message={errorMessage} setMessage={setErrorMessage}/>

                <FloatingLabel label={t("address-book.first-name")} className="text-secondary mb-3">
                    <Form.Control type="text" value={firstName} placeholder="First name"
                                  onChange={e => setFirstName(e.target.value)}/>
                </FloatingLabel>

                <FloatingLabel label={t("address-book.last-name")} className="text-secondary mb-3">
                    <Form.Control type="text" value={lastName} placeholder="Last name"
                                  onChange={e => setLastName(e.target.value)}/>
                </FloatingLabel>

                <FloatingLabel label={t("address-book.organization")} className="text-secondary mb-3">
                    <Form.Control type="text" value={organizationName} placeholder="Organization"
                                  onChange={e => setOrganizationName(e.target.value)}/>
                </FloatingLabel>

                <FloatingLabel label={t("address-book.email")} className="text-secondary mb-3">
                    <Form.Control type="email" value={email} placeholder="Email"
                                  onChange={e => setEmail(e.target.value)}/>
                </FloatingLabel>

                <Form.Label className="text-secondary">{t("address-book.phone-numbers")}</Form.Label>
                {phoneNumbers.map((number, index) => (
                    <InputGroup className="mb-2" key={index}>
                        <Form.Control type="tel" value={number} placeholder="+31612345678"
                                      onChange={e => setPhoneNumber(index, e.target.value)}/>
                        <Button variant="outline-secondary" disabled={phoneNumbers.length === 1}
                                title={t("address-book.phone-remove")}
                                onClick={() => removePhoneNumber(index)}>
                            <DeleteIcon/>
                        </Button>
                    </InputGroup>
                ))}
                <Button variant="link" className="p-0" onClick={addPhoneNumber}>
                    <AddIcon/> {t("address-book.phone-add")}
                </Button>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    {t("address-book.cancel")}
                </Button>
                <Button variant="primary" onClick={save}>
                    {t("address-book.ok")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
